import { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, Link, Switch, Typography } from '@mui/material';
import { Sidebar } from '../../Home/Sidebar';
import { SIDEBAR_SECTIONS } from '../../Home/sidebarSections';
import { PAGE_MAX_WIDTH } from '../../../theme';
import { HEIGHT_BELOW_BARS } from '../../../core/bannerOffset';
import { SIS, sisText } from '../SisMatching/sisTokens';
import { SisPill } from '../SisMatching/SisPill';
import { fetchFeatureFlags, FlagConflictError, putFeatureFlag, type FeatureFlagsList, type FlagChange, type FlagDistrict, type FlagRelease, type FlagState, type FlagView } from './featureFlagsApi';
import { cardUrl, conflictMessage, describeState, districtNames, flagPill, formatWhen, LOAD_FAILED, prUrl, releasePill, SAVE_FAILED, stageLabel } from './featureFlagsView';
import { FlagDialog, type FlagDraft } from './FlagDialog';

type Save = (key: string, change: FlagChange) => Promise<FlagState>;

type Props = {
  source?: () => Promise<FeatureFlagsList>;
  save?: Save;
};

type Editing = { key: string; releaseName: string; initial: FlagDraft; opened: number };

const column = { display: 'flex', flexDirection: 'column' } as const;

function withState(list: FeatureFlagsList, key: string, state: FlagState): FeatureFlagsList {
  const replace = (f: FlagView) => (f.key === key ? { ...f, state } : f);
  return {
    ...list,
    releases: list.releases.map((r) => ({ ...r, flags: r.flags.map(replace) })),
    unknownItems: list.unknownItems.map(replace),
  };
}

function findFlag(list: FeatureFlagsList, key: string): FlagView | null {
  for (const r of list.releases) {
    const found = r.flags.find((f) => f.key === key);
    if (found) return found;
  }
  return null;
}

function switchDraft(current: FlagState | null): FlagDraft {
  if (current && current.enabled) return { enabled: false, scope: current.scope, districtIds: current.districtIds };
  if (current && current.scope === 'districts' && current.districtIds.length > 0) return { enabled: true, scope: 'districts', districtIds: current.districtIds };
  return { enabled: true, scope: 'all', districtIds: [] };
}

function districtsDraft(current: FlagState | null): FlagDraft {
  return { enabled: true, scope: 'districts', districtIds: current?.districtIds ?? [] };
}

export function AdminFeatureFlagsPage({ source = fetchFeatureFlags, save = putFeatureFlag }: Props = {}) {
  const [list, setList] = useState<FeatureFlagsList | null>(null);
  const [failed, setFailed] = useState(false);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    source()
      .then((result) => {
        if (!cancelled) setList(result);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [source]);

  const open = useCallback((release: FlagRelease, flag: FlagView, mode: 'switch' | 'districts') => {
    setError(null);
    setEditing({ key: flag.key, releaseName: release.name, initial: mode === 'switch' ? switchDraft(flag.state) : districtsDraft(flag.state), opened: Date.now() });
  }, []);

  const confirm = async (change: FlagChange) => {
    if (!editing || !list) return;
    setSaving(true);
    setError(null);
    try {
      const next = await save(editing.key, change);
      setList((current) => (current ? withState(current, editing.key, next) : current));
      setEditing(null);
    } catch (err) {
      if (err instanceof FlagConflictError) {
        if (err.current) setList((current) => (current ? withState(current, editing.key, err.current!) : current));
        setError(conflictMessage(err.current));
      } else {
        setError(SAVE_FAILED);
      }
    } finally {
      setSaving(false);
    }
  };

  const editingFlag = editing && list ? findFlag(list, editing.key) : null;

  return (
    <Box sx={{ display: 'flex', minHeight: HEIGHT_BELOW_BARS, bgcolor: SIS.background }}>
      <Sidebar sections={SIDEBAR_SECTIONS} />
      <Box component="main" sx={{ flex: 1, minWidth: 0, padding: '24px' }}>
        <Box sx={{ maxWidth: PAGE_MAX_WIDTH, margin: '0 auto', ...column, gap: '24px' }}>
          <Box sx={{ ...column, gap: '4px' }}>
            <Typography component="h1" sx={sisText.h1}>Feature flags</Typography>
            {list !== null && (
              <Typography sx={{ ...sisText.body1, color: SIS.muted }}>{`Environment ${list.env} · as of ${formatWhen(list.asOf)} · Insight switches only; FL360's flags live in FL360.`}</Typography>
            )}
          </Box>
          {failed && <Typography sx={{ ...sisText.body1, color: SIS.muted }}>{LOAD_FAILED}</Typography>}
          {!failed && list === null && <CircularProgress data-testid="flags-loading" size={24} />}
          {list !== null && list.releases.map((release) => (
            <ReleaseCard key={release.id} release={release} districts={list.districts} busyKey={saving && editing ? editing.key : null} onSwitch={(flag) => open(release, flag, 'switch')} onDistricts={(flag) => open(release, flag, 'districts')} />
          ))}
          {list !== null && list.unknownItems.length > 0 && (
            <Box component="section" aria-label="Needs attention" data-testid="needs-attention" sx={{ bgcolor: SIS.surface, borderRadius: '8px', boxShadow: SIS.cardShadow, padding: '16px 24px', ...column, gap: '8px' }}>
              <Typography sx={sisText.h4}>Needs attention</Typography>
              <Typography sx={{ ...sisText.body2, color: SIS.muted }}>Switches stored in this environment that no release declares. They are not evaluated and cannot be changed here.</Typography>
              {list.unknownItems.map((item) => (
                <Typography key={item.key} sx={sisText.body1}>
                  <Box component="span" sx={{ fontWeight: 700 }}>{item.key}</Box>
                  {` · ${describeState(item.state)}${item.state ? ` · by ${item.state.updatedBy}: ${item.state.note}` : ''}`}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      </Box>
      {editing && list && (
        <FlagDialog
          key={`${editing.key}-${editing.opened}`}
          env={list.env}
          releaseName={editing.releaseName}
          flagKey={editing.key}
          current={editingFlag?.state ?? null}
          initial={editing.initial}
          districts={list.districts}
          saving={saving}
          error={error}
          onConfirm={confirm}
          onCancel={() => setEditing(null)}
        />
      )}
    </Box>
  );
}

function ReleaseCard({ release, districts, busyKey, onSwitch, onDistricts }: { release: FlagRelease; districts: FlagDistrict[]; busyKey: string | null; onSwitch: (flag: FlagView) => void; onDistricts: (flag: FlagView) => void }) {
  const rollup = releasePill(release.flags);
  return (
    <Box component="section" aria-label={release.name} data-testid="release-card" sx={{ bgcolor: SIS.surface, borderRadius: '8px', boxShadow: SIS.cardShadow, overflow: 'hidden' }}>
      <Box sx={{ ...column, gap: '8px', padding: '16px 24px', boxShadow: `inset 0 -1px 0 ${SIS.divider}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <Typography data-testid="release-name" sx={sisText.h4}>{release.name}</Typography>
          <SisPill pill={{ tone: 'grey', label: stageLabel(release.stage) }} />
          {rollup && <Box component="span" data-testid="release-state"><SisPill pill={rollup.pill} /></Box>}
          <Typography sx={{ ...sisText.caption, marginLeft: 'auto' }}>{`Owner ${release.owner}`}</Typography>
        </Box>
        <Typography sx={{ ...sisText.body1, color: SIS.muted }}>{release.summary}</Typography>
        {rollup?.detail && <Typography sx={{ ...sisText.body2, color: SIS.warningDark }}>{rollup.detail}</Typography>}
        {(release.cards.length > 0 || release.prs.length > 0) && (
          <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {release.cards.map((c) => <Link key={`card-${c}`} href={cardUrl(c)} target="_blank" rel="noreferrer" sx={sisText.body2}>{`Card ${c}`}</Link>)}
            {release.prs.map((p) => <Link key={`pr-${p}`} href={prUrl(p)} target="_blank" rel="noreferrer" sx={sisText.body2}>{`#${p}`}</Link>)}
          </Box>
        )}
      </Box>
      {release.flags.map((flag) => (
        <FlagRow key={flag.key} flag={flag} districts={districts} busy={busyKey === flag.key} onSwitch={() => onSwitch(flag)} onDistricts={() => onDistricts(flag)} />
      ))}
      {release.gates.map((gate, i) => (
        <Box key={`${gate.kind}-${i}`} sx={{ padding: '12px 24px', ...column, gap: '2px', '& + &': { boxShadow: `inset 0 1px 0 ${SIS.divider}` } }}>
          <Typography sx={sisText.body1}>{gate.value ? `${gate.kind}: ${gate.value}` : gate.kind}</Typography>
          {gate.note && <Typography sx={sisText.caption}>{gate.note}</Typography>}
        </Box>
      ))}
    </Box>
  );
}

function FlagRow({ flag, districts, busy, onSwitch, onDistricts }: { flag: FlagView; districts: FlagDistrict[]; busy: boolean; onSwitch: () => void; onDistricts: () => void }) {
  const { state } = flag;
  const on = state !== null && state.enabled;
  return (
    <Box data-testid={`flag-row-${flag.key}`} sx={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px 24px', '& + &': { boxShadow: `inset 0 1px 0 ${SIS.divider}` } }}>
      <Box sx={{ ...column, flex: 1, minWidth: 0, gap: '2px' }}>
        <Typography sx={sisText.body1Strong}>{flag.key}</Typography>
        <Typography sx={{ ...sisText.body2, color: SIS.muted }}>{flag.hides}</Typography>
        {state && state.scope === 'districts' && state.districtIds.length > 0 && (
          <Typography sx={sisText.caption}>{districtNames(state.districtIds, districts)}</Typography>
        )}
        {state && <Typography sx={sisText.caption}>{`Updated ${formatWhen(state.updatedAt)} by ${state.updatedBy}: ${state.note}`}</Typography>}
      </Box>
      <Box component="span" data-testid="flag-state"><SisPill pill={flagPill(state)} /></Box>
      <Typography component="button" type="button" onClick={onDistricts} disabled={busy} aria-label={`Edit districts for ${flag.key}`} sx={{ ...sisText.body2, color: SIS.primary, fontWeight: 600, border: 0, background: 'none', cursor: 'pointer', padding: 0 }}>Districts</Typography>
      <Switch checked={on} onChange={onSwitch} disabled={busy} inputProps={{ role: 'switch', 'aria-label': `Switch ${flag.key}` } as never} />
    </Box>
  );
}
